import { Dispatch, FC, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BasicModal from '../common/BasicModal';
import { Box, Button, Checkbox, CircularProgress, FormLabel, TextField, Chip } from '@mui/material';
import { CampaignContext } from '../../contexts/CampaignContext';
import { Close } from '@mui/icons-material';
import InstagramPost from '../common/InstagramPost';
import { MediaFile } from '../../interfaces/file';
import Upload from '../common/Upload';

type IForwardContentModal = {
	open: boolean;
	isChecked?: boolean;
	forwardModalInstructions: string;
	forwardModalTag: string[];
	forwardModalLink: string;
	rawModalCaption: string;
	medias: MediaFile[];
	sampleImage: (File | string)[];
	data?: { image: any; caption: string; type: string; dataType: string }[];
	setData?: Dispatch<SetStateAction<{ image: any; caption: string; type: string; dataType: string }[]>>;
	setSampleImage: React.Dispatch<React.SetStateAction<(File | string)[]>>;
	onCheckboxChange: (e: any) => void;
	onForwardModalInstructionsChange: (e: any) => void;
	onForwardModalLinkChange: (e: any) => void;
	onForwardModalTagChange: (e: any) => void;
	onSave: () => void;
	onClose: () => void;
	loading?: boolean;
	contentReady: boolean;
};

const ForwardContentModal: FC<IForwardContentModal> = ({ contentReady, loading, open, medias, forwardModalTag, setData, data, forwardModalLink, isChecked, forwardModalInstructions, sampleImage, setSampleImage, onClose, onSave, onForwardModalLinkChange, onForwardModalInstructionsChange, onCheckboxChange, onForwardModalTagChange }) => {
	const { t } = useTranslation();
	const { selectedContentType } = useContext(CampaignContext);

	const isPost = selectedContentType === 'post';

	const forwardPostTitle = t('forward_post_modal_title');
	const addStoryTitle = t('add_story_modal_title');

	const [tagInput, setTagInput] = useState<string>('');
	const [tags, setTags] = useState<string[]>(forwardModalTag);

	const isEnabled = isPost ? forwardModalInstructions && forwardModalLink : forwardModalInstructions && forwardModalLink && tags.length > 0;

	const handleAddTag = () => {
		setTags(prevTags => [...prevTags, tagInput.trim()]);
		onForwardModalTagChange({ target: { value: [...tags, tagInput.trim()] } });
		setTagInput('');
	};

	const handleTagDelete = (tagToDelete: string) => {
		const updatedTags = tags.filter(tag => tag !== tagToDelete);
		setTags(updatedTags);
		onForwardModalTagChange({ target: { value: updatedTags } });
	};

	const handleChange = (e: any) => {
		setTagInput(e.target.value);
	};

	return (
		<BasicModal className={`${loading ? 'min-h-60' : 'h-auto'} w-3/5`} open={open} onClose={onClose} title={isPost ? forwardPostTitle : addStoryTitle}>
			<Box className={`mt-8 flex gap-6 ${loading ? 'items-center' : 'justify-between'}`}>
				<Box className={`flex w-3/5 flex-col ${isPost ? 'justify-start gap-y-5' : 'justify-between gap-y-3'}`}>
					{loading ? (
						<CircularProgress size={25} className="mt-5" />
					) : (
						<>
							{!isPost && (
								<Box>
									<div className="flex items-start gap-3">
										<div className="w-full">
											<TextField fullWidth className="mb-1" onChange={handleChange} label={t('tag')} value={tagInput} />
											<span className="text-xs text-text-secondary">( {t('add_tag_into_guide')} )</span>
										</div>
										<Button size="large" onClick={handleAddTag}>
											{t('add')}
										</Button>
									</div>
									<Box className="mt-1 flex flex-wrap items-center gap-1">
										{tags.map((tag, index) => (
											<Chip key={index} label={tag} onDelete={() => handleTagDelete(tag)} deleteIcon={<Close className="mb-0.5 text-xs" />} variant="outlined" />
										))}
									</Box>
								</Box>
							)}
							<TextField fullWidth onChange={onForwardModalLinkChange} label={t('link')} value={forwardModalLink} />
							{!isPost && (
								<Box className="flex items-center justify-between rounded-lg">
									<div className="flex">
										<Checkbox size="large" className="m-0" checked={isChecked} onChange={onCheckboxChange} />
										<FormLabel className="text-base text-text-primary">{t('show_profile')}</FormLabel>
									</div>
									{!isPost && <Upload type={'media'} className="w-2/3" isPost={isPost} files={sampleImage} setFiles={setSampleImage} text={t('upload_image_way')} multiple />}
								</Box>
							)}
							<TextField fullWidth onChange={onForwardModalInstructionsChange} label={t('instructions')} multiline rows={8} value={forwardModalInstructions} />
							<Button size="large" onClick={onSave} disabled={!isEnabled}>
								{t('register')}
							</Button>
						</>
					)}
				</Box>
				<Box className="w-80">
					<InstagramPost contentReady={isPost ? contentReady : true} medias={medias} data={data} setData={setData} staticContent={true} caption={'text_your_post'} hashtag="schedule_your_hashtags" />
				</Box>
			</Box>
		</BasicModal>
	);
};

export default ForwardContentModal;
